import { ActivityGroupResponse } from '../../../../api/core/models/api/activity-group-response';
import { StoryResponse } from '../../../../api/core/models/api/story-response';
import { sumArray } from '../../../../functions/utilities/array-utility';
import { conditionallyPrefixImagesWithFilesUrl } from '../../../../functions/utilities/img-url-utility';
import { Dict } from '../../../../model/dict';
import { DashboardV4CalculationResult } from '../dashboard-calculation/dashboard-v4-calculation-result';
import { getColourFromActivityGroup, getColourFromActivityGroupCopPanorama } from './emission-sector-for-node';

export interface ActivityTree {
  id: string;
  reportedEmissions: Dict<number>;
  children: string[];
  groups: Dict<ActivityGroup>;
}

export interface ActivityGroup {
  id: string;
  title: string;
  children: string[];
  reportedEmissions: Dict<number>; //indexed on year.
  story?: StoryResponse;
  activityIds?: string[]; //if it has activityIds children is ignored.
  colour: string;
  // emissionSector?: EmissionSector;
}

export function isActivityGroup(group: ActivityGroup | ActivityTree): group is ActivityGroup {
  return !(group as ActivityTree).groups;
}

export function createActivityTree(
  rootId: string,
  calculationResult: DashboardV4CalculationResult[],
  activityGroupResponses: ActivityGroupResponse[],
  isPanorama: boolean,
  baseUrlFiles: string
): ActivityTree {
  const getColour = isPanorama ? getColourFromActivityGroupCopPanorama : getColourFromActivityGroup;
  const allGroups = activityGroupResponses.flatMap(createToGroupList(calculationResult, getColour, baseUrlFiles));
  const children = activityGroupResponses.map((g) => g.id ?? '').filter((id) => !!allGroups.find((g) => g.id === id));
  const groups = allGroups.reduce((acc: Dict<ActivityGroup>, group) => {
    acc[group.id] = group;
    return acc;
  }, {});

  return {
    id: rootId,
    reportedEmissions: sumReportedEmissionsForChildren(activityGroupResponses),
    children,
    groups,
  };
}

function getReportedEmissions(group: ActivityGroupResponse): Dict<number> {
  if (group.emissions) {
    return group.emissions.reduce((dict: Dict<number>, emissions) => {
      if (emissions.year) {
        const value: number =
          typeof emissions.value === 'number' ? emissions.value : parseFloat(emissions.value ?? '0');
        dict[emissions.year.toString()] = value ?? 0;
      }
      return dict;
    }, {});
  }
  return group.children ? sumReportedEmissionsForChildren(group.children) : {};
}

function sumReportedEmissionsForChildren(groups: ActivityGroupResponse[]): Dict<number> {
  return groups.reduce((dict: Dict<number>, child) => {
    const childEmissions = getReportedEmissions(child);
    Object.keys(childEmissions).forEach((year) => {
      dict[year] = (dict[year] ?? 0) + childEmissions[year];
    });
    return dict;
  }, {});
}

function createToGroupList(
  calculationResult: DashboardV4CalculationResult[],
  getColour: (response: ActivityGroupResponse) => string,
  baseUrlFiles: string
) {
  let colour: string;
  const toGroupList = (response: ActivityGroupResponse): ActivityGroup[] => {
    const children = structuredClone(response.children ?? []);
    //should be sorted in response, but sorting just in case.
    children.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (response.colour === undefined && response.depth === 1) {
      colour = getColour(response);
    } else {
      colour = response?.colour ?? colour;
    }
    const reportedEmissions = getReportedEmissions(response);
    const cumulativeReportedEmissions = sumArray(Object.values(reportedEmissions));
    const activities = getAllActivitiesFromResponse(response);
    const emissions = calculationResult.reduce((acc, r) => {
      return (
        acc +
        activities.reduce((a, i) => {
          return a + (r.cccEmissions[i]?.fullTransition ?? 0);
        }, 0)
      );
    }, 0);
    if (emissions === 0 && cumulativeReportedEmissions === 0) {
      return [];
    }

    const childGroups = children.flatMap(toGroupList);
    const validChildIds = children.map((c) => c.id ?? '').filter((id) => !!childGroups.find((g) => g.id === id));
    const group: ActivityGroup = {
      id: response.id ?? '',
      title: response.localisedName ?? '',
      children: validChildIds,
      reportedEmissions,
      activityIds: !response.children ? (response.activityDefinitions ?? []) : undefined,
      story: response.story && {
        ...response.story,
        imgUrl: response.story.imgUrl ? conditionallyPrefixImagesWithFilesUrl(response.story.imgUrl, baseUrlFiles) : '',
      },
      colour,
    };
    return [group].concat(childGroups);
  };
  return toGroupList;
}

export function getAllActivitiesFromResponse(response: ActivityGroupResponse): string[] {
  return response.activityDefinitions
    ? (response.activityDefinitions ?? [])
    : (response.children ?? []).flatMap((c) => getAllActivitiesFromResponse(c));
}
